import React, { useCallback, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { FlexColumn, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { NotificationService } from 'ui-components/services/notification-service';

import { messages } from './messages';
import styles from './receive-purchase-order-form.styles.module.less';
import { useWarehouseLocations } from './use-warehouse-locations';
import { ScanProduct } from '../../../../components/scan-to-receive/scan-product/scan-product';
import { useIsUnitsOfMeasureAvailable } from '../../../../feature-gates/use-is-units-of-measure-available';
import { computeAbsoluteEaches } from '../../../../constants/units-of-measure';
import Global from '../../../../store/global-selectors';
import { Workflows } from '../../../../tracking/tracking-constants';
import { useTrackWorkflow } from '../../../../tracking/use-workflow-tracking';
import { usePurchaseOrderContext } from '../purchase-order-context';
import { useReceivedProducts } from '../purchase-order-page/receive/purchase-order-receiving-context';
import { ReceivedProducts } from '../purchase-order-page/receive/received-products';

/**
 * A form component for the Receive Purchase Order feature
 * @returns {JSX.Element} The purchase order page
 */
export const ReceivePurchaseOrderForm = () => {
  const { purchaseOrder } = usePurchaseOrderContext();
  const trackWorkflow = useTrackWorkflow(Workflows.PURCHASE_ORDER_RECEIVE_FLOW);

  const [notificationId, setNotificationId] = useState();
  const { addScannedProduct } = useReceivedProducts();
  const { isAvailable: isUnitsOfMeasureEnabled } = useIsUnitsOfMeasureAvailable();
  const pendingScanMetaRef = useRef(null);

  const warehouseId = purchaseOrder?.warehouseId;
  const warehouses = useSelector(Global.selectSellerInventoryWarehouses);

  const warehouse = warehouses?.find((w) => w.inventoryWarehouseId === warehouseId);
  const warehouseName = warehouse?.name ?? '';

  const warehouseLocations = useWarehouseLocations(warehouseId);

  const removeProductNotification = useCallback(() => {
    const notifications = NotificationService.getNotifications();
    if (!notifications[notificationId]) {
      setNotificationId(undefined);
      return;
    }
    NotificationService.removeNotification(notificationId);
  }, [notificationId]);

  const getProductsForPurchaseOrder = useCallback(
    async (scanInput) => {
      pendingScanMetaRef.current = null;

      if (!purchaseOrder?.products?.length) {
        return null;
      }

      if (isUnitsOfMeasureEnabled) {
        const uomMatch = purchaseOrder.products.find(
          ({ uomSku, uomBarcode, unitsOfMeasure }) =>
            uomSku === scanInput ||
            uomBarcode === scanInput ||
            unitsOfMeasure?.some((u) => u.uomSku === scanInput || u.barcode === scanInput),
        );

        if (uomMatch) {
          const matchedUom = uomMatch.unitsOfMeasure?.find((u) => u.uomSku === scanInput || u.barcode === scanInput) ?? null;
          const targetUomId = matchedUom?.productUnitOfMeasureId ?? uomMatch.unitOfMeasure;
          const targetRow =
            purchaseOrder.products.find((p) => p.productId === uomMatch.productId && p.unitOfMeasure === targetUomId) ?? uomMatch;
          const absoluteEaches = matchedUom ? computeAbsoluteEaches(matchedUom.productUnitOfMeasureId, uomMatch.unitsOfMeasure) : null;
          pendingScanMetaRef.current = {
            absoluteEaches,
            purchaseOrderProductId: targetRow.purchaseOrderProductId ?? null,
          };
          return { product: targetRow };
        }

        const eachRow = purchaseOrder.products.find(
          ({ sku, upc, unitOfMeasure }) => (sku === scanInput || upc === scanInput) && !unitOfMeasure,
        );
        if (eachRow) {
          pendingScanMetaRef.current = {
            absoluteEaches: null,
            purchaseOrderProductId: eachRow.purchaseOrderProductId ?? null,
          };
          return { product: eachRow };
        }
      }

      const foundProduct = purchaseOrder.products.find(({ sku, upc }) => sku === scanInput || upc === scanInput);
      if (!foundProduct) {
        return null;
      }

      pendingScanMetaRef.current = {
        absoluteEaches: null,
        purchaseOrderProductId: foundProduct.purchaseOrderProductId ?? null,
      };
      return { product: foundProduct };
    },
    [isUnitsOfMeasureEnabled, purchaseOrder],
  );

  const handleSuccessfulScan = useCallback(
    (scannedProduct) => {
      const { absoluteEaches, purchaseOrderProductId } = pendingScanMetaRef.current ?? {};
      pendingScanMetaRef.current = null;
      addScannedProduct({
        ...scannedProduct,
        purchaseOrderProductId: purchaseOrderProductId ?? null,
        ...(absoluteEaches != null && { _quantityDelta: absoluteEaches }),
      });
    },
    [addScannedProduct],
  );

  return (
    <FlexColumn className={styles['drawer-section']} gap={Gap.Space8}>
      <FlexColumn className={styles['resizable-section']} gap={Gap.Space8}>
        <BannerMessageV2
          buttons={
            <Button
              className={styles.learnMoreLink}
              href='#' // TODO add link to learn more button SPD-21193
              theme='link'
            >
              <FormattedMessage {...messages.learnMore} />
            </Button>
          }
          className={styles.banner}
          content={<Text data-testid='banner-content' element='span' textType={TextType.Paragraph} value={messages.bannerContent} />}
          status={BannerStatus.Info}
        />
        <Text className={styles['instruction-text']} textType={TextType.H4} value={messages.scanInstructions} />

        <FlexColumn>
          <Text textType={TextType.Label} value={messages.warehouse} />
          <Text className={styles['warehouse-text']} textType={TextType.Description} value={warehouseName} />
        </FlexColumn>
        <ScanProduct
          disabled={!warehouseId}
          getProductMethod={getProductsForPurchaseOrder}
          onFailedScan={removeProductNotification}
          onSuccessfulScan={handleSuccessfulScan}
          trackWorkflow={trackWorkflow}
          warehouseId={warehouseId}
        />
      </FlexColumn>
      <ReceivedProducts warehouseLocations={warehouseLocations} />
    </FlexColumn>
  );
};
